import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@hub/helpers/use-store';
import { EnvService } from '@hub/services/core/env-service';
import { useTheme } from '@mui/material/styles';
import { FORBIDDEN_PRACTICES_LINKS } from '@monorepo/client-types';
import { DashboardError } from '@monorepo/nova-ui';
import { DashboardCard, LineChart, useBreakpoints } from '@monorepo/nova-ui';
import { useDashboard } from '../../../contexts';
import { ChartGradient, ChartHeader } from './components';
import { CHART_CONFIG } from './constants';
import { usePerformanceChartLogic } from './hooks';
import { PerformanceChartLoading } from './performance-chart-loading';
import {
  getChartContainerStyles,
  getChartStyles,
  getTooltipStyles,
  StyledMobileForbiddenPracticesContainer,
  StyledMobileForbiddenPracticesLink,
} from './styled';
import { FilterOption } from './types';
import { formatCurrencyValue, formatYAxisCurrencyValue } from './utils';

export const PerformanceChart: FC = () => {
  const { account, accountBalance, program } = useDashboard();

  const { t } = useTranslation(['performanceChart', 'dashboardTemplates']);
  const theme = useTheme();
  const { isMobile } = useBreakpoints();
  const {
    dataStore: { applicationConfigStore },
  } = useStore();
  const application = EnvService.application;

  const forbiddenPracticesUrl =
    applicationConfigStore.applicationConfig.tradingPracticesUrl ||
    FORBIDDEN_PRACTICES_LINKS[application];

  const {
    chartData,
    xLabels,
    yAxisDomain,
    selectedFilter,
    filterOptions,
    onFilterChange,
    isLoading,
    isError,
    refetch,
  } = usePerformanceChartLogic(account, accountBalance, program);

  if (!account) {
    return <PerformanceChartLoading />;
  }

  if (isLoading) {
    return <PerformanceChartLoading />;
  }

  if (isError) {
    return (
      <DashboardError
        title={t('error.title')}
        message={t('error.message')}
        onRetry={() => refetch()}
      />
    );
  }

  const hasData = chartData && chartData.length > 0;

  const displayChartData = hasData ? chartData : [];
  const displayXLabels = hasData
    ? xLabels
    : [
        t('chart.start', { defaultValue: 'Start' }),
        t('chart.end', { defaultValue: 'End' }),
      ];
  const displayYAxisDomain = hasData ? yAxisDomain : [0, 1000];

  return (
    <>
      <DashboardCard
        sx={getChartContainerStyles(theme)}
        my={{
          xs: 5,
          sm: 6,
        }}
      >
        <ChartHeader
          selectedFilter={selectedFilter}
          filterOptions={filterOptions}
          onFilterChange={onFilterChange}
        />

        <LineChart
          width={undefined}
          height={CHART_CONFIG.height}
          grid={{ horizontal: true }}
          series={
            hasData
              ? [
                  {
                    data: displayChartData,
                    label: filterOptions.find(
                      (opt: FilterOption) => opt.value === selectedFilter,
                    )?.label,
                    area: true,
                    baseline: 'min',
                    showMark: false,
                    id: 'balance',
                    color: theme.palette.primary.main,
                    connectNulls: false,
                    valueFormatter: (value: number | null) =>
                      value !== null ? formatCurrencyValue(value) : '',
                  },
                ]
              : []
          }
          xAxis={[
            {
              scaleType: 'point',
              data: displayXLabels,
              disableLine: true,
              disableTicks: true,
              tickLabelStyle: {
                fontSize: 0,
                textAnchor: 'middle',
              },
            },
          ]}
          yAxis={[
            {
              disableLine: true,
              disableTicks: true,
              valueFormatter: formatYAxisCurrencyValue,
              min: displayYAxisDomain?.[0],
              max: displayYAxisDomain?.[1],
              tickLabelStyle: {
                textAnchor: 'end',
                fontSize: 10,
                overflow: 'visible',
                whiteSpace: 'nowrap',
              },
            },
          ]}
          margin={CHART_CONFIG.margin}
          slotProps={{
            tooltip: {
              sx: getTooltipStyles(theme),
            },
            noDataOverlay: {
              message: t('chart.noData', {
                defaultValue: 'No data to display',
              }),
            },
          }}
          sx={getChartStyles(theme)}
        >
          {hasData && <ChartGradient />}
        </LineChart>
      </DashboardCard>
      {isMobile && (
        <StyledMobileForbiddenPracticesContainer>
          <StyledMobileForbiddenPracticesLink
            href={forbiddenPracticesUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('dashboardTemplates:forbidden_practices')}
          </StyledMobileForbiddenPracticesLink>
        </StyledMobileForbiddenPracticesContainer>
      )}
    </>
  );
};
